import gql from 'graphql-tag';
import * as Apollo from '@apollo/client';
import * as Types from '../../../server/graphql/__generated.types';

import {
  ClipsListFragmentDoc,
  ClipFragmentDoc,
  SubscriptionClipFragmentDoc,
} from '../fragments.generated';
import { ProductAccessPeriodUsageFragmentDoc } from '../ProductUsage/operation.generated';

const defaultOptions = {} as const;
export type ProjectClips_V2QueryVariables = Types.Exact<{
  projectInputs: Types.UserProjectInputs;
  clipsInputs: Types.ProjectClipsInputs_V2;
}>;

export type ProjectClips_V2Query = {
  __typename?: 'Query';
  user: {
    __typename?: 'User';
    id: string;
    project?: {
      __typename?: 'Project';
      id: string;
      clips_V2: {
        __typename?: 'ClipsConnection';
        edges: Array<{
          __typename?: 'ClipsEdge';
          cursor?: string | null;
          node: {
            __typename?: 'Clip';
            id: string;
            projectId: string;
            name: string;
            createdAt: string;
            actorId: string;
            index: number;
            sequence: string;
            fileName: string;
            text: string;
            textFormat?: string | null;
            src: string;
            isDeleted?: boolean | null;
            isCreditableBy?: string | null;
            audioFormat: string;
            combinedFrom?: Array<string> | null;
            isComplete: boolean;
            localSrc?: string | null;
            failedWithError?: string | null;
            streamRequest?: StreamRequest | null;
            ttsVersion?: string | null;
            commentCount?: number | null;
            unviewedCommentCount?: number | null;
            actor: {
              __typename?: 'ClipActor';
              id: number;
              image: string;
              name: string;
              initials: string;
              isCustomActor?: boolean | null;
              variant: {
                __typename?: 'ActorVariant';
                id: number;
                locale: string;
                version: string;
                isArchived?: boolean | null;
                variantType: {
                  __typename?: 'ActorVariantType';
                  id: Types.ActorVariantTypeId;
                  label: string;
                  description?: string | null;
                };
                versions: {
                  __typename?: 'ActorVariantVersions';
                  previous?: {
                    __typename?: 'TTSModelRelease';
                    model: string;
                    label: string;
                    description?: string | null;
                    overrideSpeakerVariantId?: number | null;
                    features: Array<string>;
                  } | null;
                  latest: {
                    __typename?: 'TTSModelRelease';
                    model: string;
                    label: string;
                    description?: string | null;
                    overrideSpeakerVariantId?: number | null;
                    features: Array<string>;
                  };
                  next?: {
                    __typename?: 'TTSModelRelease';
                    model: string;
                    label: string;
                    description?: string | null;
                    overrideSpeakerVariantId?: number | null;
                    features: Array<string>;
                  } | null;
                  preview?: {
                    __typename?: 'TTSModelRelease';
                    model: string;
                    label: string;
                    description?: string | null;
                    overrideSpeakerVariantId?: number | null;
                    features: Array<string>;
                  } | null;
                };
              };
            };
          };
          highlights?: Array<{
            __typename?: 'ClipsSearchResultHighlights';
            fieldName: string;
            highlight: string;
          }> | null;
        }>;
        pageInfo?: {
          __typename?: 'PageInfo';
          endCursor?: string | null;
          hasNextPage: boolean;
        } | null;
      };
    } | null;
  };
};

export type ReorderClipMutationVariables = Types.Exact<{
  inputs: Types.ReorderClipInputs;
}>;

export type ReorderClipMutation = {
  __typename?: 'Mutation';
  reorderClip: {
    __typename?: 'ReorderClipMutationResponse';
    code: string;
    success: boolean;
    message: string;
    clip?: { __typename?: 'Clip'; id: string; sequence: string } | null;
  };
};

export type UpdateClipMutationVariables = Types.Exact<{
  inputs: Types.UpdateClipInputs;
}>;

export type UpdateClipMutation = {
  __typename?: 'Mutation';
  updateClip: {
    __typename?: 'UpdateClipMutationResponse';
    code: string;
    success: boolean;
    message: string;
    clip?: {
      __typename?: 'Clip';
      id: string;
      projectId: string;
      name: string;
      createdAt: string;
      actorId: string;
      index: number;
      sequence: string;
      fileName: string;
      text: string;
      textFormat?: string | null;
      src: string;
      isDeleted?: boolean | null;
      isCreditableBy?: string | null;
      audioFormat: string;
      combinedFrom?: Array<string> | null;
      isComplete: boolean;
      localSrc?: string | null;
      failedWithError?: string | null;
      streamRequest?: StreamRequest | null;
      ttsVersion?: string | null;
      commentCount?: number | null;
      unviewedCommentCount?: number | null;
      actor: {
        __typename?: 'ClipActor';
        id: number;
        image: string;
        name: string;
        initials: string;
        isCustomActor?: boolean | null;
        variant: {
          __typename?: 'ActorVariant';
          id: number;
          locale: string;
          version: string;
          isArchived?: boolean | null;
          variantType: {
            __typename?: 'ActorVariantType';
            id: Types.ActorVariantTypeId;
            label: string;
            description?: string | null;
          };
          versions: {
            __typename?: 'ActorVariantVersions';
            previous?: {
              __typename?: 'TTSModelRelease';
              model: string;
              label: string;
              description?: string | null;
              overrideSpeakerVariantId?: number | null;
              features: Array<string>;
            } | null;
            latest: {
              __typename?: 'TTSModelRelease';
              model: string;
              label: string;
              description?: string | null;
              overrideSpeakerVariantId?: number | null;
              features: Array<string>;
            };
            next?: {
              __typename?: 'TTSModelRelease';
              model: string;
              label: string;
              description?: string | null;
              overrideSpeakerVariantId?: number | null;
              features: Array<string>;
            } | null;
            preview?: {
              __typename?: 'TTSModelRelease';
              model: string;
              label: string;
              description?: string | null;
              overrideSpeakerVariantId?: number | null;
              features: Array<string>;
            } | null;
          };
        };
      };
    } | null;
  };
};

export type MoveClipMutationVariables = Types.Exact<{
  inputs: Types.MoveClipsToProjectInputs;
}>;

export type MoveClipMutation = {
  __typename?: 'Mutation';
  moveClipsToProject: {
    __typename?: 'MoveClipsToProjectMutationResponse';
    code: string;
    success: boolean;
    message: string;
    clips?: Array<{
      __typename?: 'Clip';
      id: string;
      index: number;
      sequence: string;
      project: { __typename?: 'Project'; id: string; name: string };
    }> | null;
  };
};

export type CopyClipsMutationVariables = Types.Exact<{
  inputs: Types.CopyClipsToProjectInputs;
}>;

export type CopyClipsMutation = {
  __typename?: 'Mutation';
  copyClipsToProject: {
    __typename?: 'CopyClipsToProjectMutationResponse';
    code: string;
    success: boolean;
    message: string;
    clips?: Array<{
      __typename?: 'Clip';
      id: string;
      project: { __typename?: 'Project'; id: string; name: string };
    }> | null;
    userProductAccessPeriodUsage?: {
      __typename?: 'ProductAccessPeriodUsage';
      id: string;
      projects: number;
      clipsGenerated: number;
      clipsCredited: number;
      takesGenerated: number;
      takesDownloaded: number;
      downloadedTakesDurationMs: any;
      downloadedTakesTextLength: number;
      generatedTakesTextLength: number;
      generatedTakesDurationMs: any;
      folders: number;
      workspaces: number;
      periodStart: number;
      periodEnd: number;
      usageScope: Types.ProductAccessUsageScope;
    } | null;
    teamProductAccessPeriodUsage?: {
      __typename?: 'ProductAccessPeriodUsage';
      id: string;
      projects: number;
      clipsGenerated: number;
      clipsCredited: number;
      takesGenerated: number;
      takesDownloaded: number;
      downloadedTakesDurationMs: any;
      downloadedTakesTextLength: number;
      generatedTakesTextLength: number;
      generatedTakesDurationMs: any;
      folders: number;
      workspaces: number;
      periodStart: number;
      periodEnd: number;
      usageScope: Types.ProductAccessUsageScope;
    } | null;
  };
};

export type ClipEntityChangedSubscriptionVariables = Types.Exact<{
  inputs: Types.ClipEntityChangedSubscriptionInputs;
}>;

export type ClipEntityChangedSubscription = {
  __typename?: 'Subscription';
  clipEntityChanged: {
    __typename?: 'ClipEntityChangedEvent';
    previousProjectId?: string | null;
    projectId: string;
    clipId: string;
    changeType: Types.EntityChangeType;
    eventTime: string;
    clipEntity?: {
      __typename?: 'SubscriptionClip';
      id: string;
      projectId: string;
      createdAt: string;
      name: string;
      fileName: string;
      text: string;
      textFormat?: string | null;
      isDeleted?: boolean | null;
      isComplete: boolean;
      index: number;
      sequence: string;
      src: string;
      actorId: string;
      actorVariantId: string;
      audioFormat: string;
      ttsVersion?: string | null;
      combinedFrom?: Array<string> | null;
      isCreditableBy?: string | null;
      commentCount?: number | null;
      unviewedCommentCount?: number | null;
      actor: {
        __typename?: 'ClipActor';
        id: number;
        image: string;
        name: string;
        initials: string;
        isCustomActor?: boolean | null;
        variant: {
          __typename?: 'ActorVariant';
          id: number;
          locale: string;
          version: string;
          isArchived?: boolean | null;
          variantType: {
            __typename?: 'ActorVariantType';
            id: Types.ActorVariantTypeId;
            label: string;
            description?: string | null;
          };
          versions: {
            __typename?: 'ActorVariantVersions';
            previous?: {
              __typename?: 'TTSModelRelease';
              model: string;
              label: string;
              description?: string | null;
              overrideSpeakerVariantId?: number | null;
              features: Array<string>;
            } | null;
            latest: {
              __typename?: 'TTSModelRelease';
              model: string;
              label: string;
              description?: string | null;
              overrideSpeakerVariantId?: number | null;
              features: Array<string>;
            };
            next?: {
              __typename?: 'TTSModelRelease';
              model: string;
              label: string;
              description?: string | null;
              overrideSpeakerVariantId?: number | null;
              features: Array<string>;
            } | null;
            preview?: {
              __typename?: 'TTSModelRelease';
              model: string;
              label: string;
              description?: string | null;
              overrideSpeakerVariantId?: number | null;
              features: Array<string>;
            } | null;
          };
        };
      };
    } | null;
  };
};

export type DeleteClipMutationVariables = Types.Exact<{
  inputs: Types.DeleteClipInputs;
}>;

export type DeleteClipMutation = {
  __typename?: 'Mutation';
  deleteClip: {
    __typename?: 'DeleteClipMutationResponse';
    code: string;
    success: boolean;
    message: string;
    wasReCredited?: boolean | null;
    clip?: { __typename?: 'Clip'; id: string; isDeleted?: boolean | null } | null;
    userProductAccessPeriodUsage?: {
      __typename?: 'ProductAccessPeriodUsage';
      id: string;
      projects: number;
      clipsGenerated: number;
      clipsCredited: number;
      takesGenerated: number;
      takesDownloaded: number;
      downloadedTakesDurationMs: any;
      downloadedTakesTextLength: number;
      generatedTakesTextLength: number;
      generatedTakesDurationMs: any;
      folders: number;
      workspaces: number;
      periodStart: number;
      periodEnd: number;
      usageScope: Types.ProductAccessUsageScope;
    } | null;
    teamProductAccessPeriodUsage?: {
      __typename?: 'ProductAccessPeriodUsage';
      id: string;
      projects: number;
      clipsGenerated: number;
      clipsCredited: number;
      takesGenerated: number;
      takesDownloaded: number;
      downloadedTakesDurationMs: any;
      downloadedTakesTextLength: number;
      generatedTakesTextLength: number;
      generatedTakesDurationMs: any;
      folders: number;
      workspaces: number;
      periodStart: number;
      periodEnd: number;
      usageScope: Types.ProductAccessUsageScope;
    } | null;
  };
};

export type RestoreClipMutationVariables = Types.Exact<{
  inputs: Types.RestoreClipInputs;
}>;

export type RestoreClipMutation = {
  __typename?: 'Mutation';
  restoreClip: {
    __typename?: 'RestoreClipMutationResponse';
    code: string;
    success: boolean;
    message: string;
    clip?: { __typename?: 'Clip'; id: string; isDeleted?: boolean | null } | null;
    userProductAccessPeriodUsage?: {
      __typename?: 'ProductAccessPeriodUsage';
      id: string;
      projects: number;
      clipsGenerated: number;
      clipsCredited: number;
      takesGenerated: number;
      takesDownloaded: number;
      downloadedTakesDurationMs: any;
      downloadedTakesTextLength: number;
      generatedTakesTextLength: number;
      generatedTakesDurationMs: any;
      folders: number;
      workspaces: number;
      periodStart: number;
      periodEnd: number;
      usageScope: Types.ProductAccessUsageScope;
    } | null;
    teamProductAccessPeriodUsage?: {
      __typename?: 'ProductAccessPeriodUsage';
      id: string;
      projects: number;
      clipsGenerated: number;
      clipsCredited: number;
      takesGenerated: number;
      takesDownloaded: number;
      downloadedTakesDurationMs: any;
      downloadedTakesTextLength: number;
      generatedTakesTextLength: number;
      generatedTakesDurationMs: any;
      folders: number;
      workspaces: number;
      periodStart: number;
      periodEnd: number;
      usageScope: Types.ProductAccessUsageScope;
    } | null;
  };
};

export type ClipDeletionFragment = {
  __typename?: 'Clip';
  id: string;
  isDeleted?: boolean | null;
};

export type DeleteClipsMutationVariables = Types.Exact<{
  inputs: Types.DeleteClipsInputs;
}>;

export type DeleteClipsMutation = {
  __typename?: 'Mutation';
  deleteClips: {
    __typename?: 'DeleteClipsMutationResponse';
    code: string;
    success: boolean;
    message: string;
    numClipsDeleted?: number | null;
    numClipsRecredited?: number | null;
    clips?: Array<{
      __typename?: 'Clip';
      id: string;
      isDeleted?: boolean | null;
      wasReCredited?: boolean | null;
    } | null> | null;
    userProductAccessPeriodUsage?: {
      __typename?: 'ProductAccessPeriodUsage';
      id: string;
      projects: number;
      clipsGenerated: number;
      clipsCredited: number;
      takesGenerated: number;
      takesDownloaded: number;
      downloadedTakesDurationMs: any;
      downloadedTakesTextLength: number;
      generatedTakesTextLength: number;
      generatedTakesDurationMs: any;
      folders: number;
      workspaces: number;
      periodStart: number;
      periodEnd: number;
      usageScope: Types.ProductAccessUsageScope;
    } | null;
    teamProductAccessPeriodUsage?: {
      __typename?: 'ProductAccessPeriodUsage';
      id: string;
      projects: number;
      clipsGenerated: number;
      clipsCredited: number;
      takesGenerated: number;
      takesDownloaded: number;
      downloadedTakesDurationMs: any;
      downloadedTakesTextLength: number;
      generatedTakesTextLength: number;
      generatedTakesDurationMs: any;
      folders: number;
      workspaces: number;
      periodStart: number;
      periodEnd: number;
      usageScope: Types.ProductAccessUsageScope;
    } | null;
  };
};

export type RestoreClipsMutationVariables = Types.Exact<{
  inputs: Types.RestoreClipsInputs;
}>;

export type RestoreClipsMutation = {
  __typename?: 'Mutation';
  restoreClips: {
    __typename?: 'RestoreClipsMutationResponse';
    code: string;
    success: boolean;
    message: string;
    numClipsRestored?: number | null;
    clips?: Array<{
      __typename?: 'Clip';
      id: string;
      isDeleted?: boolean | null;
    } | null> | null;
    userProductAccessPeriodUsage?: {
      __typename?: 'ProductAccessPeriodUsage';
      id: string;
      projects: number;
      clipsGenerated: number;
      clipsCredited: number;
      takesGenerated: number;
      takesDownloaded: number;
      downloadedTakesDurationMs: any;
      downloadedTakesTextLength: number;
      generatedTakesTextLength: number;
      generatedTakesDurationMs: any;
      folders: number;
      workspaces: number;
      periodStart: number;
      periodEnd: number;
      usageScope: Types.ProductAccessUsageScope;
    } | null;
    teamProductAccessPeriodUsage?: {
      __typename?: 'ProductAccessPeriodUsage';
      id: string;
      projects: number;
      clipsGenerated: number;
      clipsCredited: number;
      takesGenerated: number;
      takesDownloaded: number;
      downloadedTakesDurationMs: any;
      downloadedTakesTextLength: number;
      generatedTakesTextLength: number;
      generatedTakesDurationMs: any;
      folders: number;
      workspaces: number;
      periodStart: number;
      periodEnd: number;
      usageScope: Types.ProductAccessUsageScope;
    } | null;
  };
};

export type CombineClipsMutationVariables = Types.Exact<{
  inputs: Types.CombineClipsInputs;
}>;

export type CombineClipsMutation = {
  __typename?: 'Mutation';
  combineClips: {
    __typename?: 'CombineClipsMutationResponse';
    code: string;
    success: boolean;
    message: string;
    baseClip?: {
      __typename?: 'BaseClipType';
      id: string;
      projectId: string;
      name: string;
      createdAt: string;
      actorId: string;
      actorVariantId: string;
      index: number;
      sequence: string;
      fileName: string;
      text: string;
      textFormat?: string | null;
      src: string;
      isDeleted?: boolean | null;
      isComplete: boolean;
      isCreditableBy?: string | null;
      audioFormat: string;
      ttsVersion?: string | null;
      combinedFrom?: Array<string> | null;
    } | null;
  };
};

export const ClipDeletionFragmentDoc = gql`
  fragment ClipDeletionFragment on Clip {
    id
    isDeleted
  }
`;
export const ProjectClips_V2Document = gql`
  query ProjectClips_V2(
    $projectInputs: UserProjectInputs!
    $clipsInputs: ProjectClipsInputs_V2!
  ) {
    user {
      id
      project(inputs: $projectInputs) {
        id
        clips_V2(inputs: $clipsInputs) {
          ...ClipsListFragment
        }
      }
    }
  }
  ${ClipsListFragmentDoc}
`;

/**
 * __useProjectClips_V2Query__
 *
 * To run a query within a React component, call `useProjectClips_V2Query` and pass it any options that fit your needs.
 * When your component renders, `useProjectClips_V2Query` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useProjectClips_V2Query({
 *   variables: {
 *      projectInputs: // value for 'projectInputs'
 *      clipsInputs: // value for 'clipsInputs'
 *   },
 * });
 */
export function useProjectClips_V2Query(
  baseOptions: Apollo.QueryHookOptions<
    ProjectClips_V2Query,
    ProjectClips_V2QueryVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<ProjectClips_V2Query, ProjectClips_V2QueryVariables>(
    ProjectClips_V2Document,
    options
  );
}
export function useProjectClips_V2LazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<
    ProjectClips_V2Query,
    ProjectClips_V2QueryVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<ProjectClips_V2Query, ProjectClips_V2QueryVariables>(
    ProjectClips_V2Document,
    options
  );
}
export type ProjectClips_V2QueryHookResult = ReturnType<typeof useProjectClips_V2Query>;
export type ProjectClips_V2LazyQueryHookResult = ReturnType<
  typeof useProjectClips_V2LazyQuery
>;
export type ProjectClips_V2QueryResult = Apollo.QueryResult<
  ProjectClips_V2Query,
  ProjectClips_V2QueryVariables
>;
export const ReorderClipDocument = gql`
  mutation reorderClip($inputs: ReorderClipInputs!) {
    reorderClip(inputs: $inputs) {
      code
      success
      message
      clip {
        id
        sequence
      }
    }
  }
`;
export type ReorderClipMutationFn = Apollo.MutationFunction<
  ReorderClipMutation,
  ReorderClipMutationVariables
>;

/**
 * __useReorderClipMutation__
 *
 * To run a mutation, you first call `useReorderClipMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useReorderClipMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [reorderClipMutation, { data, loading, error }] = useReorderClipMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useReorderClipMutation(
  baseOptions?: Apollo.MutationHookOptions<
    ReorderClipMutation,
    ReorderClipMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<ReorderClipMutation, ReorderClipMutationVariables>(
    ReorderClipDocument,
    options
  );
}
export type ReorderClipMutationHookResult = ReturnType<typeof useReorderClipMutation>;
export type ReorderClipMutationResult = Apollo.MutationResult<ReorderClipMutation>;
export type ReorderClipMutationOptions = Apollo.BaseMutationOptions<
  ReorderClipMutation,
  ReorderClipMutationVariables
>;
export const UpdateClipDocument = gql`
  mutation updateClip($inputs: UpdateClipInputs!) {
    updateClip(inputs: $inputs) {
      code
      success
      message
      clip {
        ...ClipFragment
      }
    }
  }
  ${ClipFragmentDoc}
`;
export type UpdateClipMutationFn = Apollo.MutationFunction<
  UpdateClipMutation,
  UpdateClipMutationVariables
>;

/**
 * __useUpdateClipMutation__
 *
 * To run a mutation, you first call `useUpdateClipMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateClipMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateClipMutation, { data, loading, error }] = useUpdateClipMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useUpdateClipMutation(
  baseOptions?: Apollo.MutationHookOptions<
    UpdateClipMutation,
    UpdateClipMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<UpdateClipMutation, UpdateClipMutationVariables>(
    UpdateClipDocument,
    options
  );
}
export type UpdateClipMutationHookResult = ReturnType<typeof useUpdateClipMutation>;
export type UpdateClipMutationResult = Apollo.MutationResult<UpdateClipMutation>;
export type UpdateClipMutationOptions = Apollo.BaseMutationOptions<
  UpdateClipMutation,
  UpdateClipMutationVariables
>;
export const MoveClipDocument = gql`
  mutation moveClip($inputs: MoveClipsToProjectInputs!) {
    moveClipsToProject(inputs: $inputs) {
      code
      success
      message
      clips {
        id
        index
        sequence
        project {
          id
          name
        }
      }
    }
  }
`;
export type MoveClipMutationFn = Apollo.MutationFunction<
  MoveClipMutation,
  MoveClipMutationVariables
>;

/**
 * __useMoveClipMutation__
 *
 * To run a mutation, you first call `useMoveClipMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useMoveClipMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [moveClipMutation, { data, loading, error }] = useMoveClipMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useMoveClipMutation(
  baseOptions?: Apollo.MutationHookOptions<MoveClipMutation, MoveClipMutationVariables>
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<MoveClipMutation, MoveClipMutationVariables>(
    MoveClipDocument,
    options
  );
}
export type MoveClipMutationHookResult = ReturnType<typeof useMoveClipMutation>;
export type MoveClipMutationResult = Apollo.MutationResult<MoveClipMutation>;
export type MoveClipMutationOptions = Apollo.BaseMutationOptions<
  MoveClipMutation,
  MoveClipMutationVariables
>;
export const CopyClipsDocument = gql`
  mutation copyClips($inputs: CopyClipsToProjectInputs!) {
    copyClipsToProject(inputs: $inputs) {
      code
      success
      message
      clips {
        id
        project {
          id
          name
        }
      }
      userProductAccessPeriodUsage {
        ...ProductAccessPeriodUsageFragment
      }
      teamProductAccessPeriodUsage {
        ...ProductAccessPeriodUsageFragment
      }
    }
  }
  ${ProductAccessPeriodUsageFragmentDoc}
`;
export type CopyClipsMutationFn = Apollo.MutationFunction<
  CopyClipsMutation,
  CopyClipsMutationVariables
>;

/**
 * __useCopyClipsMutation__
 *
 * To run a mutation, you first call `useCopyClipsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCopyClipsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [copyClipsMutation, { data, loading, error }] = useCopyClipsMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useCopyClipsMutation(
  baseOptions?: Apollo.MutationHookOptions<CopyClipsMutation, CopyClipsMutationVariables>
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<CopyClipsMutation, CopyClipsMutationVariables>(
    CopyClipsDocument,
    options
  );
}
export type CopyClipsMutationHookResult = ReturnType<typeof useCopyClipsMutation>;
export type CopyClipsMutationResult = Apollo.MutationResult<CopyClipsMutation>;
export type CopyClipsMutationOptions = Apollo.BaseMutationOptions<
  CopyClipsMutation,
  CopyClipsMutationVariables
>;
export const ClipEntityChangedDocument = gql`
  subscription ClipEntityChanged($inputs: ClipEntityChangedSubscriptionInputs!) {
    clipEntityChanged(inputs: $inputs) {
      previousProjectId
      projectId
      clipId
      changeType
      eventTime
      clipEntity {
        ...SubscriptionClipFragment
      }
    }
  }
  ${SubscriptionClipFragmentDoc}
`;

/**
 * __useClipEntityChangedSubscription__
 *
 * To run a query within a React component, call `useClipEntityChangedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useClipEntityChangedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useClipEntityChangedSubscription({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useClipEntityChangedSubscription(
  baseOptions: Apollo.SubscriptionHookOptions<
    ClipEntityChangedSubscription,
    ClipEntityChangedSubscriptionVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSubscription<
    ClipEntityChangedSubscription,
    ClipEntityChangedSubscriptionVariables
  >(ClipEntityChangedDocument, options);
}
export type ClipEntityChangedSubscriptionHookResult = ReturnType<
  typeof useClipEntityChangedSubscription
>;
export type ClipEntityChangedSubscriptionResult =
  Apollo.SubscriptionResult<ClipEntityChangedSubscription>;
export const DeleteClipDocument = gql`
  mutation deleteClip($inputs: DeleteClipInputs!) {
    deleteClip(inputs: $inputs) {
      code
      success
      message
      clip {
        id
        isDeleted
      }
      wasReCredited
      userProductAccessPeriodUsage {
        ...ProductAccessPeriodUsageFragment
      }
      teamProductAccessPeriodUsage {
        ...ProductAccessPeriodUsageFragment
      }
    }
  }
  ${ProductAccessPeriodUsageFragmentDoc}
`;
export type DeleteClipMutationFn = Apollo.MutationFunction<
  DeleteClipMutation,
  DeleteClipMutationVariables
>;

/**
 * __useDeleteClipMutation__
 *
 * To run a mutation, you first call `useDeleteClipMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteClipMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteClipMutation, { data, loading, error }] = useDeleteClipMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useDeleteClipMutation(
  baseOptions?: Apollo.MutationHookOptions<
    DeleteClipMutation,
    DeleteClipMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<DeleteClipMutation, DeleteClipMutationVariables>(
    DeleteClipDocument,
    options
  );
}
export type DeleteClipMutationHookResult = ReturnType<typeof useDeleteClipMutation>;
export type DeleteClipMutationResult = Apollo.MutationResult<DeleteClipMutation>;
export type DeleteClipMutationOptions = Apollo.BaseMutationOptions<
  DeleteClipMutation,
  DeleteClipMutationVariables
>;
export const RestoreClipDocument = gql`
  mutation restoreClip($inputs: RestoreClipInputs!) {
    restoreClip(inputs: $inputs) {
      code
      success
      message
      clip {
        ...ClipDeletionFragment
      }
      userProductAccessPeriodUsage {
        ...ProductAccessPeriodUsageFragment
      }
      teamProductAccessPeriodUsage {
        ...ProductAccessPeriodUsageFragment
      }
    }
  }
  ${ClipDeletionFragmentDoc}
  ${ProductAccessPeriodUsageFragmentDoc}
`;
export type RestoreClipMutationFn = Apollo.MutationFunction<
  RestoreClipMutation,
  RestoreClipMutationVariables
>;

/**
 * __useRestoreClipMutation__
 *
 * To run a mutation, you first call `useRestoreClipMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRestoreClipMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [restoreClipMutation, { data, loading, error }] = useRestoreClipMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useRestoreClipMutation(
  baseOptions?: Apollo.MutationHookOptions<
    RestoreClipMutation,
    RestoreClipMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<RestoreClipMutation, RestoreClipMutationVariables>(
    RestoreClipDocument,
    options
  );
}
export type RestoreClipMutationHookResult = ReturnType<typeof useRestoreClipMutation>;
export type RestoreClipMutationResult = Apollo.MutationResult<RestoreClipMutation>;
export type RestoreClipMutationOptions = Apollo.BaseMutationOptions<
  RestoreClipMutation,
  RestoreClipMutationVariables
>;
export const DeleteClipsDocument = gql`
  mutation deleteClips($inputs: DeleteClipsInputs!) {
    deleteClips(inputs: $inputs) {
      code
      success
      message
      numClipsDeleted
      numClipsRecredited
      clips {
        id
        isDeleted
        wasReCredited
      }
      userProductAccessPeriodUsage {
        ...ProductAccessPeriodUsageFragment
      }
      teamProductAccessPeriodUsage {
        ...ProductAccessPeriodUsageFragment
      }
    }
  }
  ${ProductAccessPeriodUsageFragmentDoc}
`;
export type DeleteClipsMutationFn = Apollo.MutationFunction<
  DeleteClipsMutation,
  DeleteClipsMutationVariables
>;

/**
 * __useDeleteClipsMutation__
 *
 * To run a mutation, you first call `useDeleteClipsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteClipsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteClipsMutation, { data, loading, error }] = useDeleteClipsMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useDeleteClipsMutation(
  baseOptions?: Apollo.MutationHookOptions<
    DeleteClipsMutation,
    DeleteClipsMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<DeleteClipsMutation, DeleteClipsMutationVariables>(
    DeleteClipsDocument,
    options
  );
}
export type DeleteClipsMutationHookResult = ReturnType<typeof useDeleteClipsMutation>;
export type DeleteClipsMutationResult = Apollo.MutationResult<DeleteClipsMutation>;
export type DeleteClipsMutationOptions = Apollo.BaseMutationOptions<
  DeleteClipsMutation,
  DeleteClipsMutationVariables
>;
export const RestoreClipsDocument = gql`
  mutation restoreClips($inputs: RestoreClipsInputs!) {
    restoreClips(inputs: $inputs) {
      code
      success
      message
      numClipsRestored
      clips {
        ...ClipDeletionFragment
      }
      userProductAccessPeriodUsage {
        ...ProductAccessPeriodUsageFragment
      }
      teamProductAccessPeriodUsage {
        ...ProductAccessPeriodUsageFragment
      }
    }
  }
  ${ClipDeletionFragmentDoc}
  ${ProductAccessPeriodUsageFragmentDoc}
`;
export type RestoreClipsMutationFn = Apollo.MutationFunction<
  RestoreClipsMutation,
  RestoreClipsMutationVariables
>;

/**
 * __useRestoreClipsMutation__
 *
 * To run a mutation, you first call `useRestoreClipsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRestoreClipsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [restoreClipsMutation, { data, loading, error }] = useRestoreClipsMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useRestoreClipsMutation(
  baseOptions?: Apollo.MutationHookOptions<
    RestoreClipsMutation,
    RestoreClipsMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<RestoreClipsMutation, RestoreClipsMutationVariables>(
    RestoreClipsDocument,
    options
  );
}
export type RestoreClipsMutationHookResult = ReturnType<typeof useRestoreClipsMutation>;
export type RestoreClipsMutationResult = Apollo.MutationResult<RestoreClipsMutation>;
export type RestoreClipsMutationOptions = Apollo.BaseMutationOptions<
  RestoreClipsMutation,
  RestoreClipsMutationVariables
>;
export const CombineClipsDocument = gql`
  mutation combineClips($inputs: CombineClipsInputs!) {
    combineClips(inputs: $inputs) {
      code
      success
      message
      baseClip {
        id
        projectId
        name
        createdAt
        actorId
        actorVariantId
        index
        sequence
        fileName
        text
        textFormat
        src
        isDeleted
        isComplete
        isCreditableBy
        audioFormat
        ttsVersion
        combinedFrom
      }
    }
  }
`;
export type CombineClipsMutationFn = Apollo.MutationFunction<
  CombineClipsMutation,
  CombineClipsMutationVariables
>;

/**
 * __useCombineClipsMutation__
 *
 * To run a mutation, you first call `useCombineClipsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCombineClipsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [combineClipsMutation, { data, loading, error }] = useCombineClipsMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useCombineClipsMutation(
  baseOptions?: Apollo.MutationHookOptions<
    CombineClipsMutation,
    CombineClipsMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<CombineClipsMutation, CombineClipsMutationVariables>(
    CombineClipsDocument,
    options
  );
}
export type CombineClipsMutationHookResult = ReturnType<typeof useCombineClipsMutation>;
export type CombineClipsMutationResult = Apollo.MutationResult<CombineClipsMutation>;
export type CombineClipsMutationOptions = Apollo.BaseMutationOptions<
  CombineClipsMutation,
  CombineClipsMutationVariables
>;
